/**
 * @fileoverview Authentication context and provider for the Aswan application.
 *
 * Provides authentication state management including user info, login/logout
 * functionality, and token persistence via localStorage.
 *
 * @example
 * ```tsx
 * // Wrap app with provider
 * <AuthProvider>
 *   <App />
 * </AuthProvider>
 *
 * // Use in components
 * const { user, login, logout, isLoading } = useAuth();
 * ```
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import {
  api,
  setAuthToken,
  clearAuthToken,
  getAuthToken,
  type UserInfo,
  type UserRole,
} from '../api/client';

/**
 * Authentication state interface.
 */
export interface AuthState {
  /** Currently authenticated user, or null if not authenticated */
  user: UserInfo | null;
  /** Whether authentication state is being loaded/verified */
  isLoading: boolean;
  /** Whether user is currently authenticated */
  isAuthenticated: boolean;
  /** Most recent authentication error message */
  error: string | null;
}

/**
 * Authentication context value interface.
 */
export interface AuthContextValue extends AuthState {
  /**
   * Attempts to log in with the provided credentials.
   * On success, stores the JWT token and updates user state.
   *
   * @param email - User's email address
   * @param password - User's password
   * @returns Promise that resolves when login completes (success or failure)
   */
  login: (email: string, password: string) => Promise<boolean>;

  /**
   * Logs out the current user.
   * Clears the stored token and resets user state.
   */
  logout: () => void;

  /**
   * Clears any authentication error.
   */
  clearError: () => void;

  /**
   * Checks if the current user has one of the specified roles.
   *
   * @param roles - Roles to check against
   * @returns True if user has any of the specified roles
   */
  hasRole: (...roles: UserRole[]) => boolean;
}

/**
 * Authentication context.
 * Use the useAuth hook to access this context.
 */
const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Props for the AuthProvider component.
 */
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * JWT payload structure for token decoding.
 * We only decode to extract user info, validation is done server-side.
 */
interface JwtPayload {
  userId: string;
  tenantId: string;
  email: string;
  name: string;
  role: UserRole;
  exp: number;
}

/**
 * Decodes a JWT token to extract the payload.
 * Note: This does NOT validate the token - that's done server-side.
 *
 * @param token - The JWT token to decode
 * @returns The decoded payload, or null if decoding fails
 */
function decodeJwtPayload(token: string): JwtPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3 || !parts[1]) {
      return null;
    }
    const payload = JSON.parse(atob(parts[1])) as JwtPayload;
    return payload;
  } catch {
    return null;
  }
}

/**
 * Extracts UserInfo from a JWT payload.
 *
 * @param payload - Decoded JWT payload
 * @returns UserInfo object
 */
function payloadToUserInfo(payload: JwtPayload): UserInfo {
  return {
    userId: payload.userId,
    tenantId: payload.tenantId,
    email: payload.email,
    name: payload.name,
    role: payload.role,
  };
}

/**
 * Checks if a JWT token has expired.
 *
 * @param payload - Decoded JWT payload
 * @returns True if the token has expired
 */
function isTokenExpired(payload: JwtPayload): boolean {
  // exp is in seconds, Date.now() is in milliseconds
  return payload.exp * 1000 < Date.now();
}

/**
 * Authentication provider component.
 * Wraps the application to provide authentication state and methods.
 *
 * On mount, checks for an existing token in localStorage and validates it.
 * If the token is still valid, restores the user session.
 *
 * @param props - Provider props with children
 * @param props.children
 * @returns Provider component wrapping children
 */
export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check for existing valid token on mount
  useEffect(() => {
    const token = getAuthToken();
    if (token) {
      const payload = decodeJwtPayload(token);
      if (payload && !isTokenExpired(payload)) {
        // Token exists and appears valid, restore user session
        setUser(payloadToUserInfo(payload));
      } else {
        // Token is invalid or expired, clear it
        clearAuthToken();
      }
    }
    setIsLoading(false);
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      // TODO: Replace hardcoded tenantSlug with dynamic tenant selection
      // The tenantSlug is required by the backend but not in the OpenAPI spec
      const result = await api.POST('/api/auth/login', {
        body: { email, password, tenantSlug: 'demo-engine-services' } as { email: string; password: string; tenantSlug?: string },
      });

      // Check if we got a successful response with data
      if (result.data) {
        // Store token and update user state
        setAuthToken(result.data.token);
        setUser(result.data.user);
        setIsLoading(false);
        return true;
      }

      // Handle error response
      const status = result.response?.status ?? 0;
      let errorMessage: string;

      if (status === 401) {
        errorMessage = 'Invalid email or password';
      } else if (result.error && typeof result.error === 'object' && 'message' in result.error) {
        errorMessage = (result.error as { message: string }).message;
      } else if (status >= 500) {
        errorMessage = 'Server error. Please try again later.';
      } else {
        errorMessage = 'Login failed. Please try again.';
      }

      setError(errorMessage);
      setIsLoading(false);
      return false;
    } catch (err) {
      // Handle network or unexpected errors
      const message = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(message);
      setIsLoading(false);
      return false;
    }
  }, []);

  const logout = useCallback(() => {
    clearAuthToken();
    setUser(null);
    setError(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const hasRole = useCallback(
    (...roles: UserRole[]): boolean => {
      if (!user) return false;
      return roles.includes(user.role);
    },
    [user]
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      isLoading,
      isAuthenticated: user !== null,
      error,
      login,
      logout,
      clearError,
      hasRole,
    }),
    [user, isLoading, error, login, logout, clearError, hasRole]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access the authentication context.
 * Must be used within an AuthProvider.
 *
 * @returns The authentication context value
 * @throws Error if used outside of AuthProvider
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { user, login, logout, isAuthenticated } = useAuth();
 *
 *   if (!isAuthenticated) {
 *     return <LoginPrompt />;
 *   }
 *
 *   return <div>Welcome, {user.name}!</div>;
 * }
 * ```
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Re-export types for convenience
export type { UserInfo, UserRole };
